/** @format */
import axios, { AxiosError, type AxiosRequestHeaders } from 'axios';

type SanitizableConfig = Readonly<NonNullable<AxiosError['config']>>;

const sanitizeAxiosConfig = (config: SanitizableConfig): SanitizableConfig => ({
    url: config.url,
    method: config.method,
    baseURL: config.baseURL,
    timeout: config.timeout,
    headers: {} as AxiosRequestHeaders,
});

export const sanitizeAxiosError = <ErrorType>(
    error: ErrorType
): ErrorType | AxiosError => {
    if (!axios.isAxiosError(error)) {
        return error;
    }

    // Clone the original error to sanitize in isolation
    const sanitizedError: AxiosError = Object.create(
        Object.getPrototypeOf(error),
        Object.getOwnPropertyDescriptors(error)
    );

    delete sanitizedError.request;

    if (sanitizedError.config) {
        sanitizedError.config = sanitizeAxiosConfig(sanitizedError.config);
    }

    if (sanitizedError.response) {
        const sanitizedResponse = { ...sanitizedError.response };

        // `response.request` is the underlying `http.ClientRequest`, whose
        // `.agent.sockets` is the same process-shared socket map this
        // function exists to keep out of anything downstream
        delete sanitizedResponse.request;
        sanitizedResponse.headers = {};
        sanitizedResponse.config = sanitizeAxiosConfig(
            sanitizedResponse.config
        );

        sanitizedError.response = sanitizedResponse;
    }

    const sanitizedAxiosError = new AxiosError(
        sanitizedError.message,
        sanitizedError.code,
        sanitizedError.config,
        undefined,
        sanitizedError.response
    );

    Object.defineProperty(sanitizedAxiosError, 'stack', {
        value: error.stack,
        writable: true,
        configurable: true,
        enumerable: false,
    });

    return sanitizedAxiosError;
};
